import { useEffect, useState } from "react";
import { X, Save, Loader2, FlaskConical, BadgeCheck, AlertTriangle, Database, ExternalLink, Activity, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";

/**
 * Stripe Settings Modal
 * --------------------------------------------------------------
 * Operator UI for db.settings.stripe_config:
 *   - currency  (3-letter ISO 4217, e.g. chf/eur/usd)
 *   - price_essential_3 / price_cinema_6 / price_premiere_12
 *
 * Backend reads DB-first, falls back to .env, then to defaults.
 * Empty input → DB row $unset → .env fallback re-engages.
 *
 * Props:
 *   open:       boolean
 *   onClose:    () => void
 *   onSaved:    () => void  (parent should refetch sidebar badge)
 */
export default function StripeSettingsModal({ open, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [config, setConfig] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // {ok, message, ...}
  const [form, setForm] = useState({
    currency: "",
    price_essential_3: "",
    price_cinema_6: "",
    price_premiere_12: "",
  });

  // Fetch current config when modal opens
  useEffect(() => {
    if (!open) return;
    setErr(""); setOkMsg("");
    setLoading(true);
    api.get("/saas/admin/stripe-config")
      .then((res) => {
        setConfig(res.data);
        const db = res.data?.db_overrides || {};
        setForm({
          currency:          db.currency          || "",
          price_essential_3: db.price_essential_3 || "",
          price_cinema_6:    db.price_cinema_6    || "",
          price_premiere_12: db.price_premiere_12 || "",
        });
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Yüklenemedi."))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const runStripeTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.get("/saas/admin/stripe-test");
      setTestResult(res.data);
    } catch (e) {
      setTestResult({ ok: false, code: "request_error", message: e?.response?.data?.detail || "Test çağrısı başarısız." });
    } finally {
      setTesting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setOkMsg(""); setSaving(true);
    try {
      const res = await api.put("/saas/admin/stripe-config", {
        // Always send all fields so empty strings unset DB rows correctly.
        currency:          form.currency.trim().toLowerCase(),
        price_essential_3: form.price_essential_3.trim(),
        price_cinema_6:    form.price_cinema_6.trim(),
        price_premiere_12: form.price_premiere_12.trim(),
      });
      setOkMsg("Kaydedildi. Yeni değerler anında etkili.");
      // Refresh local view of effective values
      setConfig((c) => c ? { ...c, effective: res.data?.effective || c.effective } : c);
      onSaved && onSaved();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  const mode = config?.stripe_mode;
  const ModeIcon = mode === "live" ? BadgeCheck : mode === "unset" ? AlertTriangle : FlaskConical;
  const modeColor = mode === "live"
    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
    : mode === "unset"
      ? "bg-red-500/15 border-red-500/40 text-red-300"
      : "bg-amber-500/15 border-amber-500/40 text-amber-300";

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/75 backdrop-blur-sm grid place-items-center p-6"
      onClick={onClose}
      data-testid="stripe-settings-backdrop"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-xl bg-slate-900 border border-amber-500/30 rounded-2xl shadow-2xl shadow-amber-500/10 max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="stripe-settings-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Database className="w-4 h-4 text-amber-400" /> Stripe Yapılandırması
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">DB'den çözülür, .env fallback. Restart gerektirmez.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-white"
            data-testid="stripe-settings-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto space-y-5">
          {/* Mode banner */}
          {mode && (
            <div className={`flex items-center gap-3 p-3 rounded-lg border ${modeColor}`} data-testid="stripe-settings-mode">
              <ModeIcon className="w-4 h-4 shrink-0" />
              <div className="flex-1 text-xs">
                <div className="font-mono font-bold uppercase tracking-wider">
                  {mode === "live" ? "LIVE MODE" : mode === "unset" ? "STRIPE_API_KEY YOK" : "TEST MODE"}
                </div>
                <div className="opacity-80">
                  Webhook: {config?.webhook_secured ? "imzalı" : "imzasız (stub)"}
                  {mode === "live" && " · Gerçek ödemeler aktif!"}
                  {mode === "test" && " · Sandbox — gerçek ödeme alınmaz"}
                </div>
              </div>
              <button
                type="button"
                onClick={runStripeTest}
                disabled={testing}
                className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-950/60 border border-slate-700 text-[11px] font-bold text-white hover:border-amber-500/50 transition disabled:opacity-50"
                data-testid="stripe-settings-test-btn"
              >
                {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />}
                {testing ? "Test ediliyor…" : "Bağlantıyı Test Et"}
              </button>
            </div>
          )}

          {/* Test result banner */}
          {testResult && (
            <div
              className={`flex items-start gap-3 p-3 rounded-lg border text-xs ${
                testResult.ok
                  ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                  : "bg-red-500/10 border-red-500/40 text-red-300"
              }`}
              data-testid="stripe-settings-test-result"
              data-ok={testResult.ok ? "1" : "0"}
            >
              {testResult.ok
                ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                : <XCircle      className="w-4 h-4 shrink-0 mt-0.5" />}
              <div className="flex-1 space-y-0.5">
                <div className="font-bold">{testResult.ok ? "Bağlantı OK" : "Bağlantı Hatası"}</div>
                <div className="opacity-90 leading-relaxed">{testResult.message}</div>
                {testResult.ok && (
                  <div className="text-[10px] font-mono opacity-80 pt-1 space-x-2">
                    {testResult.account_id && <span>acct: {testResult.account_id.slice(0, 12)}…</span>}
                    {testResult.country     && <span>· {testResult.country}</span>}
                    {testResult.default_currency && <span>· stripe: {testResult.default_currency.toUpperCase()}</span>}
                    {testResult.configured_currency && <span>· config: {testResult.configured_currency.toUpperCase()}</span>}
                    {testResult.charges_enabled !== undefined && (
                      <span className={testResult.charges_enabled ? "text-emerald-400" : "text-amber-400"}>
                        · charges {testResult.charges_enabled ? "açık" : "kapalı"}
                      </span>
                    )}
                    {testResult.currency_match === false && (
                      <span className="block text-amber-400 mt-1">⚠ Stripe hesabınız {testResult.default_currency?.toUpperCase()} kullanıyor — config'iniz {testResult.configured_currency?.toUpperCase()}. Bazı ödemeler reddedilebilir.</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {loading ? (
            <div className="py-10 flex items-center justify-center text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <>
              {/* Currency */}
              <Field
                label="Default Currency"
                hint="3 hane ISO 4217 (chf, eur, usd, gbp …). Boş bırakırsanız .env'deki STRIPE_CHECKOUT_CURRENCY kullanılır."
                value={form.currency}
                onChange={(v) => setForm({ ...form, currency: v.toLowerCase().replace(/[^a-z]/g, "").slice(0, 4) })}
                placeholder="chf"
                effective={config?.effective?.currency}
                testId="stripe-settings-currency"
              />

              {/* Price IDs */}
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold pt-2">
                Stripe Price IDs (opsiyonel — set edilirse inline amount yerine bu Price ile checkout açılır)
              </div>
              <Field
                label="Essential — 3 Ay"
                hint="price_... formatı"
                value={form.price_essential_3}
                onChange={(v) => setForm({ ...form, price_essential_3: v })}
                placeholder="price_xxxxxxxxxx"
                effective={config?.effective?.price_essential_3}
                mono
                testId="stripe-settings-price-essential-3"
              />
              <Field
                label="Cinema — 6 Ay"
                hint="price_... formatı"
                value={form.price_cinema_6}
                onChange={(v) => setForm({ ...form, price_cinema_6: v })}
                placeholder="price_xxxxxxxxxx"
                effective={config?.effective?.price_cinema_6}
                mono
                testId="stripe-settings-price-cinema-6"
              />
              <Field
                label="Premiere — 12 Ay"
                hint="price_... formatı"
                value={form.price_premiere_12}
                onChange={(v) => setForm({ ...form, price_premiere_12: v })}
                placeholder="price_xxxxxxxxxx"
                effective={config?.effective?.price_premiere_12}
                mono
                testId="stripe-settings-price-premiere-12"
              />

              <div className="text-[11px] text-gray-500 leading-relaxed border-l-2 border-amber-500/40 pl-3">
                <strong className="text-amber-400">Not:</strong> Price ID'lerini Stripe Dashboard → Products → Prices'tan alabilirsiniz.&nbsp;
                <a
                  href="https://dashboard.stripe.com/products"
                  target="_blank" rel="noreferrer"
                  className="text-amber-400 hover:underline inline-flex items-center gap-1"
                >
                  Stripe Dashboard <ExternalLink size={11} />
                </a>
                . Boş bırakılan Price ID'lerde plan inline amount/currency ile sandbox akışını kullanır.
              </div>

              {err   && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="stripe-settings-error">{err}</div>}
              {okMsg && <div className="text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3" data-testid="stripe-settings-ok">{okMsg}</div>}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-950/40 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 h-10 rounded-lg border border-slate-700 text-white font-semibold hover:bg-slate-800"
            data-testid="stripe-settings-cancel"
          >
            Vazgeç
          </button>
          <button
            type="submit"
            disabled={saving || loading}
            className="px-5 h-10 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
            data-testid="stripe-settings-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save size={14} />}
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </form>
    </div>
  );
}


function Field({ label, hint, value, onChange, placeholder, effective, mono = false, testId }) {
  // Show what's currently EFFECTIVE so the operator sees what /api/saas/checkout
  // will actually use right now (could be DB override or .env fallback).
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs font-bold text-gray-300">{label}</span>
        {effective !== undefined && effective !== null && effective !== "" && effective !== value && (
          <span className="text-[10px] text-gray-500">
            Aktif: <span className={`text-amber-400 ${mono ? "font-mono" : ""}`}>{effective}</span>
          </span>
        )}
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        spellCheck={false}
        autoComplete="off"
        className={`w-full bg-slate-950 border border-slate-800 rounded-lg p-2.5 text-white text-sm focus:border-amber-500 focus:outline-none ${mono ? "font-mono" : ""}`}
        data-testid={testId}
      />
      <p className="text-[10px] text-gray-500 mt-1">{hint}</p>
    </label>
  );
}
